import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { Router } from '@angular/router';
import { ApiService } from '../net/apiRoute';
import ngZh from '@angular/common/locales/zh';
import ngEn from '@angular/common/locales/en';
import ngZhTw from '@angular/common/locales/zh-Hant';

import { TranslateService } from '@ngx-translate/core';
import { registerLocaleData } from '@angular/common';
import { ResponseContext } from '../net/response-context.interface';
import { en_US, NzI18nService, zh_CN, zh_TW } from 'ng-zorro-antd/i18n';
import { LiveRoomInfo } from '../common/app-im/model';

export interface Layout {
  /** 是否折叠右边菜单 */
  collapsed: boolean;
  lang: string;
  collapsedWidth: number;
  [key: string]: any;
  theme: string;
}
export interface SkinTheme {

  name: string;
  text: string;

}
export interface App {
  [key: string]: any;
  name?: string;
  description?: string;
  version: string;
  showLang: boolean;
}

export interface User {
  name?: string;
  avatar?: string;

  email?: string;
  tag?: string;
  phone?: string;
  signature?: string;
  organizeName?: string;
  departmentName?: string;
  address?: string;
  [key: string]: any;
}

export interface Menu {
  items: Array<MenuItem>;
}

export interface MenuItem {
  menuId?: string;
  title?: string;
  parentId?: string;
  icon?: string;
  routerLink?: string;
  sort?: string;
  type: number;
  submenu?: Array<MenuItem>;
}

interface LangData {
  sortname: string;
  text: string;
  ng: any;
  zorro: any;

}
const DEFAULT = 'zh-CN';
const LANGS: { [key: string]: LangData } = {
  'zh-CN': {
    sortname: 'CN',
    text: '简体中文',
    ng: ngZh,
    zorro: zh_CN,
  },
  'zh-TW': {
    sortname: 'HK',
    text: '繁体中文',
    ng: ngZhTw,
    zorro: zh_TW,
  },
  'en-US': {
    sortname: 'GB',
    text: 'English',
    ng: ngEn,
    zorro: en_US
  },
};

const DEFAULT_SKIN = 'aliyun';
const THEMES: Array<SkinTheme> = [
  {
    name: 'default',
    text: '默认主题'
  },
  {
    name: 'dark',
    text: '深色主题'
  }, {
    name: 'aliyun',
    text: '阿里云主题'
  }

];
export const LAYOUT = 'layout';
export const USER = 'user';
export const MENU = 'menu';
export const MESSAGEBOX = 'messageBox'
export const APP = "app";

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  //app
  private _app: App | null = null;
  // 布局
  private _layout: Layout | null = null;
  // 用户
  private _user: User | null = null;
  // 菜单
  private _menu: Menu | null = null;

  //消息组件
  private _messageBox: MessageBoxSetting | null = null;

  //直播房间信息
  private _liveRoom: LiveRoomInfo | null = null;
  // 是否第一次登陆过来
  public firstLogin: Boolean;
  //语言支持
  public langs: any[];
  //最后一次错误信息
  public lastErrorMessage: ResponseContext<string> = { Status: 0, Message: '', Result: '' };
  //默认语言
  private _defaultLange = DEFAULT;
  //是否是移动端
  private _isMobile: boolean | null = null;
  public themes: Array<SkinTheme> = THEMES;
  public _defaultTheme = DEFAULT_SKIN;
  private notify$ = new Subject<any>();
  constructor(private translate: TranslateService, private nzI18nService: NzI18nService, private router: Router) {

    this.langs = this._langs;
    const defaultLan = this.layout.lang || translate.getBrowserLang();
    // `@ngx-translate/core` 预先知道支持哪些语言
    const lans = this._langs.map(item => item.code);
    translate.addLangs(lans);
    this._defaultLange = lans.find(n => n == defaultLan) ? defaultLan : lans[0];
    this.updateLangData(this._defaultLange);
    this._defaultTheme = this.layout.theme;

  }

  private updateLangData(lang: string) {
    const item = LANGS[lang];
    registerLocaleData(item.ng);
    this.nzI18nService.setLocale(item.zorro);
    // (window as any).__locale__ = item.dateFns;

  }
  //使用语言
  useLang(lang: string): void {
    lang = lang || this.translate.getDefaultLang();
    if (this.currentLang === lang) return;
    this.updateLangData(lang);
    this.translate.use(lang).subscribe(() => this.notify$.next(lang));
    this.setLayout('lang', lang);
  }

  private removeUnusedTheme(theme: string): void {
    document.documentElement.classList.remove(theme);
    const removedThemeStyle = document.getElementById(theme);
    if (removedThemeStyle) {
      document.head.removeChild(removedThemeStyle);
    }
  }

  private loadCss(href: string, id: string): Promise<Event> {
    return new Promise((resolve, reject) => {
      const style = document.createElement('link');
      style.rel = 'stylesheet';
      style.href = href;
      style.id = id;
      style.onload = resolve;
      style.onerror = reject;
      document.head.append(style);
    });
  }

  public loadTheme(theme, firstLoad = true): Promise<Event> {

    if (firstLoad) {
      document.documentElement.classList.add(theme);
    }
    return new Promise<Event>((resolve, reject) => {
      this.loadCss(`${theme}.css`, theme).then(
        (e) => {
          if (!firstLoad) {
            document.documentElement.classList.add(theme);
          }
          console.log(this._defaultTheme + "    " + theme)
          if (this._defaultTheme != theme) {
            this.removeUnusedTheme(this._defaultTheme);
          }
          resolve(e);
        },
        (e) => reject(e)
      );
    });
  }

  useTheme(theme: string): void {
    if (this._defaultTheme === theme) return;
    this.loadTheme(theme).then(() => {
      this._defaultTheme = theme;
      this.setLayout('theme', theme);

    });


  }

  /** 获取语言列表 */
  getLangs() {
    return this._langs;
  }
  /** 翻译 */
  fanyi(key: string, interpolateParams?: Object) {
    if (!key) {
      return key;
    }
    return this.translate.instant(key, interpolateParams);
  }
  /** 默认语言 */
  get defaultLang() {
    return this._defaultLange;
  }
  /** 当前语言 */
  get currentLang() {
    return (
      this.translate.currentLang ||
      this.translate.getDefaultLang() ||
      this._defaultLange
    );
  }
  get user(): User {
    if (!this._user) {
      this._user = { ...this.get(USER) };
      this.set(USER, this._user);
    }
    return this._user as User;
  }

  setUser(value: User) {
    this._user = value;
    this.set(USER, value);
    this.notify$.next({ type: 'user', value });
    return true;
  }

  setMenu(value: Menu) {
    this._menu = value;
    this.set(MENU, value);
    this.notify$.next({ type: 'menu', value });
    return true;
  }

  get menu(): Menu {
    if (!this._menu) {
      this._menu = { ...this.get(MENU) };
      this.set(MENU, this._menu);
    }
    return this._menu as Menu;
  }


  public get(key: string) {
    var d = localStorage.getItem(key) || 'null';
    if (!d) {
      localStorage.clear();
      this.router.navigate(['/passport/login']);

    }
    return JSON.parse(d) || null;
  }

  public set(key: string, value: any) {
    localStorage.setItem(key, JSON.stringify(value));
  }
  public delete(key: string) {
    localStorage.removeItem(key);
  }


  get layout(): Layout {
    if (!this._layout) {
      this._layout = {
        fixed: true,
        collapsed: false,
        collapsedWidth: 80,
        boxed: false,
        lang: this.defaultLang,
        theme: this._defaultTheme,
        ...this.get(LAYOUT),
      };
      this.set(LAYOUT, this._layout);
    }
    return this._layout as Layout;
  }

  setLayout(name: string | Layout, value?: any): boolean {
    if (typeof name === 'string') {
      this.layout[name] = value;
    } else {
      this._layout = name;
    }
    this.set(LAYOUT, this._layout);
    this.notify$.next({ type: 'layout', name, value } as any);
    return true;
  }

  //是否移动终端
  get isReduce() {
    return document.documentElement.clientWidth < 800;
  }
  //返回APP信息
  get app(): App {
    if (!this._app) {
      this._app = {
        year: new Date().getFullYear(),
        ...this.get(APP),
      };
      this.set(APP, this._app);
    }
    return this._app as App;
  }
  //设置App信息
  setApp(value: App) {
    this._app = value;
    this.set(APP, value);
    this.notify$.next({ type: 'app', value });
    return true;
  }

  private _langs = Object.keys(LANGS).map(code => {
    return { code, sortname: LANGS[code].sortname, text: LANGS[code].text };
  });



  get messageBox(): MessageBoxSetting {
    if (!this._messageBox) {

      this._messageBox = {
        selectIndex: 1,
        show: false,
        showLive: false,
        shardScreen: false,
        max: false,
        lastWidth:'810px',
        lastHeight:'556px',
        width:'810px',
        height:'556px',
        top:'auto',
        lastTop:'auto',

        left:'auto',
        lastLeft:'30px',
        right:'30px',
        lastRight:'30px',
        bottom:'84px',
        lastBottom:'84px',
        ...this.get(MESSAGEBOX)
      };
      this.set(MESSAGEBOX, this._messageBox);
    }
    return this._messageBox;
  }


  setMessageBox(name: string | MessageBoxSetting, value?: any): boolean {
    if (typeof name === 'string') {
      this._messageBox[name] = value;
      
    } else {
      this._messageBox = name;
    }
    this.set(MESSAGEBOX, this._messageBox);
    this.notify$.next({ type: 'messageBox', name, value } as any);
    return true;
  }


  get liveInfo(): LiveRoomInfo {
    if (!this._liveRoom) {
      this._liveRoom = {
        Title: this.user.name + '的直播',
        HeadImg: this.user.avatar,
        LikeNumber: 0,
        OnlineUser: 0,
        Show: false,
        Screen: false,
        Audio: false,
        Camera: false,
        IsCreator: false,
        BanMessage:false
      };

    }
    return this._liveRoom;
  }

  setLiveRoomInfo(name: string | LiveRoomInfo, value?: any) {
    if (typeof name === 'string') {
      this._liveRoom[name] = value;
    } else {
      this._liveRoom.Id = name.Id;
      this._liveRoom.UserId = name.UserId;
      this._liveRoom.Title = name.Title;
      this._liveRoom.RoomId = name.RoomId;
      this._liveRoom.Status = name.Status;
      this._liveRoom.StartDate = name.StartDate;
      this._liveRoom.EndDate = name.EndDate;
      this._liveRoom.OnlineUser = name.OnlineUser;
      this._liveRoom.LikeNumber = name.LikeNumber;
      this._liveRoom.HeadImg = name.HeadImg;
      this._liveRoom.Screen = name.Screen;
      this._liveRoom.Audio = name.Audio;
      this._liveRoom.Camera = name.Camera;
      this._liveRoom.BanMessage=name.BanMessage;
    }
    this.notify$.next({ type: 'liveBox', name, value } as any);
  }

  get liveStatus(): boolean {
    return this._liveRoom.Audio || this._liveRoom.Camera || this._liveRoom.Screen;
  }


  get isMobile(): boolean {
    if (this._isMobile == null) {
      this._isMobile=/Android|webOS|iPhone|iPod|BlackBerry/i.test(navigator.userAgent);
    }
    return this._isMobile;
  }

}

export interface MessageBoxSetting {
  width:string;
  height:string;
  lastWidth:number;
  lastHeight:number;
  top: string;
  lastTop:string;
  left: string;
  lastLeft:string;
  right: string;
  lastRight:string;
  bottom: string;
  lastBottom:string;
  selectIndex: number;
  show: boolean;
  max: boolean;
}


